// NodeMetadataFooter.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './NodeMetadataFooter.css';

// Components
import EnumPillSelect from 'components/enumpillselect/EnumPillSelect';
import Pill from 'components/pill/Pill';

/**
 * NodeMetadataFooter
 *
 * Renders optional leading pills (source / database) plus enum / multiselect / tags pills.
 * Single-select enums are editable when canEdit + onNodeChange are provided.
 *
 * @param {object} node - The node whose metadata should be displayed.
 * @param {array} enumPills - The resolved enum pills to render.
 * @param {array} leadingPills - Optional pills rendered before enum pills (source, database).
 * @param {string} addon - Optional visual addon.
 * @param {boolean} [canEdit=false] - Whether enum pills may be edited.
 * @param {function} [onNodeChange] - (nodeKey, patch) => void.
 * @param {string} [language='en'] - Language for enum option labels.
 *
 * @returns {ReactNode} The rendered metadata footer.
 */
const NodeMetadataFooter = React.memo(({
  node,
  enumPills = [],
  leadingPills = [],
  addon = 'default',
  canEdit = false,
  onNodeChange = null,
  language = 'en',
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  if (!node) return null;

  const visibleEnumPills = Array.isArray(enumPills)
    ? enumPills.filter((pill) => {
      if (!pill) return false;
      // Hide empty placeholders when the user cannot edit.
      if (pill.isEmpty && !(canEdit && pill.editable && typeof onNodeChange === 'function')) {
        return false;
      }
      return true;
    })
    : [];
  // Leading pills are currently not rendered; only show the footer when enum pills exist.
  if (visibleEnumPills.length === 0) return null;

  const typeKey = String(node?.type || '').trim().toUpperCase();
  const enumPillFontSize = addon === 'peek' ? '12px' : '12px';

  // ----- RENDER ----- //

  return (
    <div className={`node-metadata-footer-container${addon ? ` is-${addon}` : ''}`}>

      <div className="node-metadata-footer-pills">

        {/** 
        {hasLeading ? leadingPills.map((pill) => (
          <Pill
            key={pill.key}
            label={pill.label}
            color={pill.color || theme.panel}
            iconKey={pill.iconKey}
            iconSrc={pill.iconSrc}
            labelColor={pill.labelColor || theme.foreground}
            clickable={pill.clickable}
            onClick={pill.onClick}
            title={pill.title}
            className={pill.className || ''}
          />
        )) : null}
         */}

        {visibleEnumPills.map((pill) => {
          const showSelect = Boolean(
            canEdit
            && pill.editable
            && typeof onNodeChange === 'function'
            && pill.field,
          );

          if (showSelect) {
            return (
              <EnumPillSelect
                key={pill.key}
                field={pill.field}
                value={pill.value}
                node={node}
                typeKey={typeKey}
                canEdit
                onNodeChange={onNodeChange}
                placeholder={pill.placeholder || `${pill.label || 'Select'}…`}
                language={language}
                fontSize={enumPillFontSize}
              />
            );
          }

          return (
            <Pill
              key={pill.key}
              label={pill.label}
              color={pill.color || theme.outlineMuted}
              iconKey={pill.iconKey}
              labelColor={pill.color ? theme.highlightForeground : theme.foreground}
              title={pill.title || ''}
            />
          );
        })}
      </div>

    </div>
  );
});

// Dev Tools
NodeMetadataFooter.displayName = 'NodeMetadataFooter';

export default NodeMetadataFooter;
